import React, { useState, useEffect } from "react";
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
} from "firebase/firestore";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import firebaseConfig from "../firebaseConfig";

// Import chart components from Chart.js and react-chartjs-2
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const provider = new GoogleAuthProvider();

// ---------- Simple "Encryption" Function ----------
// (Utilities below are kept as in your original file)
const M = 100000007; // A prime number greater than any date number (YYYYMMDD)
const A = 7919; // Multiplier (should be coprime with M)
const B = 12345; // Adder

function modInverse(a, m) {
  let m0 = m,
    x0 = 0,
    x1 = 1;
  if (m === 1) return 0;
  while (a > 1) {
    let q = Math.floor(a / m);
    let t = m;
    m = a % m;
    a = t;
    t = x0;
    x0 = x1 - q * x0;
    x1 = t;
  }
  if (x1 < 0) x1 += m0;
  return x1;
}

function encryptDate(dateStr) {
  const numStr = dateStr.replace(/-/g, "");
  const x = parseInt(numStr, 10);
  const y = (x * A + B) % M;
  let hex = y.toString(16).padStart(8, "0");
  return hex.split("").reverse().join("");
}

// A simple Spinner component with inline styles
const Spinner = () => (
  <div
    style={{
      width: "50px",
      height: "50px",
      border: "6px solid #f3f3f3",
      borderTop: "6px solid #4285F4",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    }}
  ></div>
);

// Add keyframes for spinner animation
const spinnerKeyframes = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

// New component for Daily Statistics with Bar Chart, Navigation, and a fixed container
const StatsBlock = () => {
  // Default to today's date in YYYY-MM-DD format
  const todayDate = new Date().toISOString().substring(0, 10);
  const [statsDate, setStatsDate] = useState(todayDate);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const fetchStats = async (date) => {
    setLoadingStats(true);
    const colRef = collection(db, "userGameStates");
    const snapshot = await getDocs(colRef);
    let solvedCount = 0;
    let failedCount = 0;
    let solvedDistribution = {};
    let failedDistribution = {};

    snapshot.docs.forEach((doc) => {
      // Assumes doc.id is in the format "userId-YYYY-MM-DD"
      if (doc.id.slice(-date.length) === date) {
        const data = doc.data();
        if (data.completed) {
          const mistakes = 10 - (data.triesLeft ?? 10);
          if (data.failed) {
            failedCount++;
            failedDistribution[mistakes] =
              (failedDistribution[mistakes] || 0) + 1;
          } else {
            solvedCount++;
            solvedDistribution[mistakes] =
              (solvedDistribution[mistakes] || 0) + 1;
          }
        }
      }
    });
    setStats({
      solved: solvedCount,
      failed: failedCount,
      solvedDistribution,
      failedDistribution,
    });
    setLoadingStats(false);
  };

  useEffect(() => {
    if (statsDate) {
      fetchStats(statsDate);
    }
  }, [statsDate]);

  // Navigation button handlers
  const handlePrevDay = () => {
    const current = new Date(statsDate);
    current.setDate(current.getDate() - 1);
    const newDateStr = current.toISOString().substring(0, 10);
    setStatsDate(newDateStr);
  };

  const handleNextDay = () => {
    const current = new Date(statsDate);
    const today = new Date(todayDate);
    if (current < today) {
      current.setDate(current.getDate() + 1);
      const newDateStr = current.toISOString().substring(0, 10);
      setStatsDate(newDateStr);
    }
  };

  // Prepare chart data: merge keys from both distributions
  const labels = stats
    ? Array.from(
        new Set([
          ...Object.keys(stats.solvedDistribution),
          ...Object.keys(stats.failedDistribution),
        ])
      ).sort((a, b) => a - b)
    : [];

  const chartData = {
    labels,
    datasets: [
      {
        label: "Solved",
        data: labels.map((label) => stats?.solvedDistribution[label] || 0),
        backgroundColor: "rgba(75, 192, 192, 0.5)",
      },
      {
        label: "Failed",
        data: labels.map((label) => stats?.failedDistribution[label] || 0),
        backgroundColor: "rgba(255, 99, 132, 0.5)",
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Mistakes Distribution",
      },
    },
  };

  // Inline styles for navigation buttons
  const navButtonStyle = {
    background: "#4285F4",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "0.5rem 1rem",
    cursor: "pointer",
  };

  const navContainerStyle = {
    display: "flex",
    justifyContent: "center",
    gap: "1rem",
    marginBottom: "1rem",
  };

  // Fixed container style for the stats block
  const containerFixedStyle = {
    marginTop: "2rem",
    padding: "1rem",
    background: "#e0f7fa",
    borderRadius: "8px",
    width: "100%",
    maxWidth: "600px",
    minHeight: "400px",
  };

  return (
    <div style={containerFixedStyle}>
      {/* Include spinner keyframes */}
      <style>{spinnerKeyframes}</style>
      <h2 style={{ textAlign: "center" }}>Daily Statistics</h2>
      <div style={{ textAlign: "center", marginBottom: "1rem" }}>
        <label htmlFor="statsDate">Selected Date: </label>
        <input
          type="date"
          id="statsDate"
          value={statsDate}
          onChange={(e) => setStatsDate(e.target.value)}
          style={{
            padding: "0.5rem",
            borderRadius: "4px",
            border: "1px solid #ccc",
            marginLeft: "0.5rem",
          }}
        />
      </div>
      <div style={navContainerStyle}>
        <button onClick={handlePrevDay} style={navButtonStyle}>
          Previous Day
        </button>
        <button
          onClick={handleNextDay}
          style={navButtonStyle}
          disabled={statsDate === new Date().toISOString().substring(0, 10)}
        >
          Next Day
        </button>
      </div>
      {loadingStats ? (
        <div
          style={{
            height: "380px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spinner />
        </div>
      ) : (
        stats && (
          <div>
            <p>Solved: {stats.solved}</p>
            <p>Failed: {stats.failed}</p>
            <Bar data={chartData} options={options} />
          </div>
        )
      )}
    </div>
  );
};

const AdminPage = () => {
  // Only allow this email
  const allowedEmail = "liavkuperman@gmail.com";

  // Auth state
  const [user, setUser] = useState(null);
  const [authorized, setAuthorized] = useState(false);

  // Admin form state
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().substring(0, 10);
  const [riddleDate, setRiddleDate] = useState(tomorrowStr);
  const [riddleIndex, setRiddleIndex] = useState("");
  const [categories, setCategories] = useState([
    { categoryName: "", words: ["", "", "", ""] },
    { categoryName: "", words: ["", "", "", ""] },
    { categoryName: "", words: ["", "", "", ""] },
    { categoryName: "", words: ["", "", "", ""] },
  ]);

  // List of existing riddles from Firestore. Each item is an object: { date, index }
  const [existingDates, setExistingDates] = useState([]);
  const [loadingRiddle, setLoadingRiddle] = useState(false);

  // New state for all categories from every riddle
  const [allCategories, setAllCategories] = useState([]);

  // Inline styles
  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "2rem",
    fontFamily: "Arial, sans-serif",
    background: "#f5f5f5",
    minHeight: "100vh",
  };

  const cardStyle = {
    background: "#fff",
    padding: "2rem",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    width: "100%",
    maxWidth: "600px",
    marginBottom: "2rem",
  };

  const headingStyle = {
    textAlign: "center",
    marginBottom: "1rem",
    color: "#333",
  };

  const buttonStyle = {
    padding: "0.75rem 1.5rem",
    background: "#4285F4",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "1rem",
    marginTop: "1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const signOutButtonStyle = {
    ...buttonStyle,
    background: "#db4437",
    marginTop: "0.5rem",
  };

  const inputStyle = {
    width: "100%",
    padding: "0.5rem",
    marginBottom: "1rem",
    borderRadius: "4px",
    border: "1px solid #ccc",
    fontSize: "1rem",
  };

  const linkStyle = {
    display: "block",
    marginTop: "1rem",
    textAlign: "center",
    fontSize: "1rem",
    color: "#4285F4",
    textDecoration: "none",
    wordBreak: "break-all",
  };

  // Set persistence so that the user remains logged in
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch((error) => {
      console.error("Error setting auth persistence", error);
    });
  }, []);

  // Automatically detect auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        if (currentUser.email === allowedEmail) {
          setAuthorized(true);
        } else {
          signOut(auth);
        }
      } else {
        setUser(null);
        setAuthorized(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Sign in with Google. Only allowed if the email matches.
  const signIn = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const signedInUser = result.user;
      setUser(signedInUser);
      if (signedInUser.email === allowedEmail) {
        setAuthorized(true);
      } else {
        alert("Not authorized");
        signOut(auth);
      }
    } catch (error) {
      console.error("Error signing in", error);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    setUser(null);
    setAuthorized(false);
  };

  // Fetch existing riddles from Firestore (each with its date and index)
  useEffect(() => {
    const fetchDates = async () => {
      const colRef = collection(db, "connections_words");
      const snapshot = await getDocs(colRef);
      const riddles = snapshot.docs.map((doc) => ({
        date: doc.id,
        index: doc.data().index,
      }));
      // Sort riddles descending by date (most recent first)
      riddles.sort((a, b) => new Date(b.date) - new Date(a.date));
      setExistingDates(riddles);
    };
    fetchDates();
  }, []);

  // New useEffect to fetch all distinct categories from all riddles
  useEffect(() => {
    const fetchAllCategories = async () => {
      const colRef = collection(db, "connections_words");
      const snapshot = await getDocs(colRef);
      const categoriesSet = new Set();
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.words && Array.isArray(data.words)) {
          data.words.forEach((item) => {
            if (item.category && item.category.trim() !== "") {
              categoriesSet.add(item.category.trim());
            }
          });
        }
      });
      setAllCategories([...categoriesSet]);
    };
    fetchAllCategories();
  }, []);

  // Auto-fill riddleIndex when adding a new riddle
  useEffect(() => {
    if (!riddleDate || !existingDates.some((r) => r.date === riddleDate)) {
      if (existingDates.length > 0) {
        const latest = existingDates[0];
        const latestIndex = parseInt(latest.index, 10) || 0;
        setRiddleIndex((latestIndex + 1).toString());
      } else {
        setRiddleIndex("1");
      }
    }
  }, [existingDates, riddleDate]);

  // Handle change for categories form
  const handleCategoryChange = (catIdx, field, value, wordIdx = null) => {
    setCategories((prev) => {
      const newCats = [...prev];
      if (field === "categoryName") {
        newCats[catIdx].categoryName = value;
      } else if (field === "word" && wordIdx !== null) {
        newCats[catIdx].words[wordIdx] = value;
      }
      return newCats;
    });
  };

  // Submit handler: combine the words and save to Firestore
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!riddleDate) {
      alert("Please choose a date");
      return;
    }
    const combinedWords = [];
    categories.forEach((cat) => {
      cat.words.forEach((w) => {
        combinedWords.push({ word: w, category: cat.categoryName });
      });
    });
    const data = {
      index: riddleIndex,
      words: combinedWords,
    };
    try {
      await setDoc(doc(db, "connections_words", riddleDate), data);
      alert("Riddle saved successfully");
      const colRef = collection(db, "connections_words");
      const snapshot = await getDocs(colRef);
      const riddles = snapshot.docs.map((doc) => ({
        date: doc.id,
        index: doc.data().index,
      }));
      riddles.sort((a, b) => new Date(b.date) - new Date(a.date));
      setExistingDates(riddles);
    } catch (error) {
      console.error("Error saving riddle", error);
      alert("Error saving riddle");
    }
  };

  // Load an existing riddle into the form for editing
  const handleLoadRiddle = async (date) => {
    setLoadingRiddle(true);
    try {
      const docRef = doc(db, "connections_words", date);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setRiddleDate(date);
        setRiddleIndex(data.index || "");
        const groups = {};
        data.words.forEach((item) => {
          if (!groups[item.category]) {
            groups[item.category] = [];
          }
          groups[item.category].push(item.word);
        });
        const newCategories = [];
        Object.keys(groups).forEach((cat) => {
          newCategories.push({
            categoryName: cat,
            words:
              groups[cat].length === 4
                ? groups[cat]
                : [...groups[cat], "", "", "", ""].slice(0, 4),
          });
        });
        while (newCategories.length < 4) {
          newCategories.push({ categoryName: "", words: ["", "", "", ""] });
        }
        setCategories(newCategories);
      } else {
        alert("No riddle found for that date");
      }
    } catch (error) {
      console.error("Error loading riddle", error);
      alert("Error loading riddle");
    }
    setLoadingRiddle(false);
  };

  // New function to copy all riddles in an organized format to the clipboard
  const copyRiddlesToClipboard = async () => {
    try {
      const colRef = collection(db, "connections_words");
      const snapshot = await getDocs(colRef);
      let riddlesText = "";
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        let riddleText = `Date: ${docSnap.id}\nIndex: ${data.index}\n`;
        if (data.words && Array.isArray(data.words)) {
          // Group the words by category
          const categoriesMap = {};
          data.words.forEach((item) => {
            const category = item.category?.trim() || "No Category";
            if (!categoriesMap[category]) {
              categoriesMap[category] = [];
            }
            categoriesMap[category].push(item.word);
          });
          riddleText += "Categories:\n";
          for (const cat in categoriesMap) {
            riddleText += `  ${cat}: ${categoriesMap[cat].join(", ")}\n`;
          }
        }
        riddleText += "\n------------------\n\n";
        riddlesText += riddleText;
      });
      await navigator.clipboard.writeText(riddlesText);
      alert("Riddles copied to clipboard!");
    } catch (error) {
      console.error("Error copying riddles to clipboard: ", error);
      alert("Failed to copy riddles to clipboard");
    }
  };

  // Component to display all distinct categories as colored chips
  const CategoriesBlock = () => {
    const chipColors = [
      "#ff8a80",
      "#ffd180",
      "#ffff8d",
      "#80d8ff",
      "#ccff90",
      "#ea80fc",
    ];
    return (
      <div style={{ ...cardStyle, background: "#fff", marginBottom: "2rem" }}>
        <h2 style={{ ...headingStyle, fontSize: "1.25rem" }}>
          All Riddle Categories
        </h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
          {allCategories.length > 0 ? (
            allCategories.map((cat, idx) => (
              <div
                key={idx}
                style={{
                  background: chipColors[idx % chipColors.length],
                  padding: "0.5rem 1rem",
                  borderRadius: "20px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                {cat}
              </div>
            ))
          ) : (
            <p>No categories found.</p>
          )}
        </div>
      </div>
    );
  };

  if (!user || !authorized) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h1 style={headingStyle}>Admin Login</h1>
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src="https://developers.google.com/static/identity/images/branding_guideline_sample_lt_sq_lg.svg"
              alt="Google Logo"
              onClick={signIn}
              style={{ cursor: "pointer" }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h1 style={headingStyle}>Admin Panel - Daily Riddle Editor</h1>
        <button style={signOutButtonStyle} onClick={handleSignOut}>
          Sign Out
        </button>
        <h2 style={{ ...headingStyle, fontSize: "1.25rem", marginTop: "1rem" }}>
          {loadingRiddle ? "Loading..." : "Edit/Create Riddle"}
        </h2>
        <form
          onSubmit={handleSubmit}
          style={{ marginBottom: "2rem", width: "100%" }}
        >
          <div>
            <label>Date: </label>
            <input
              type="date"
              value={riddleDate}
              onChange={(e) => setRiddleDate(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label>Index: </label>
            <input
              type="text"
              value={riddleIndex}
              onChange={(e) => setRiddleIndex(e.target.value)}
              style={inputStyle}
            />
          </div>
          {categories.map((cat, catIdx) => (
            <div
              key={catIdx}
              style={{
                border: "1px solid #ccc",
                padding: "1rem",
                marginTop: "1rem",
                borderRadius: "4px",
              }}
            >
              <div>
                <label>Category {catIdx + 1} Name: </label>
                <input
                  type="text"
                  value={cat.categoryName}
                  onChange={(e) =>
                    handleCategoryChange(catIdx, "categoryName", e.target.value)
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                {cat.words.map((word, wordIdx) => (
                  <div key={wordIdx} style={{ marginBottom: "0.5rem" }}>
                    <label>Word {wordIdx + 1}: </label>
                    <input
                      type="text"
                      value={word}
                      onChange={(e) =>
                        handleCategoryChange(
                          catIdx,
                          "word",
                          e.target.value,
                          wordIdx
                        )
                      }
                      style={inputStyle}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button type="submit" style={buttonStyle}>
            Save Riddle
          </button>
        </form>
        <h2 style={{ ...headingStyle, fontSize: "1.25rem" }}>
          Existing Riddles
        </h2>
        <ul style={{ listStyle: "none", padding: 0 }}>
          {existingDates.map((riddle) => (
            <li key={riddle.date} style={{ marginBottom: "0.5rem" }}>
              <a
                href="#"
                onClick={() => handleLoadRiddle(riddle.date)}
                style={{
                  color: "#4285F4",
                  textDecoration: "none",
                  fontSize: "1rem",
                }}
              >
                {riddle.date} - {riddle.index}
              </a>
            </li>
          ))}
        </ul>
        {/* New button to copy all riddles */}
        <button onClick={copyRiddlesToClipboard} style={buttonStyle}>
          Copy All Riddles to Clipboard
        </button>
      </div>
      <CategoriesBlock />
      {/* Daily Statistics Block */}
      <StatsBlock />
    </div>
  );
};

export default AdminPage;
